import React from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BopRowActions — Wrapper standard per azioni riga in tabelle BOP
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Standard UX BOP v3 (vedi .claude/BOP-FRONTEND-SKILL.md):
 * - La RIGA INTERA apre il dettaglio (onClick su <tr>)
 * - Questa colonna contiene solo azioni DIVERSE dalla navigazione:
 *   elimina, duplica, stampa, scarica, ecc.
 * - NON includere "Apri/Visualizza/Dettaglio" qui (ridondante col click riga)
 *
 * Ogni azione gestisce automaticamente stopPropagation() per non triggerare
 * la navigazione della riga.
 *
 * Usage:
 * ```tsx
 * <td>
 *   <BopRowActions
 *     actions={[
 *       { icon: <EditIcon />, label: 'Modifica', onClick: () => openEdit(item), variant: 'primary' },
 *       { icon: <TrashIcon />, label: 'Elimina', onClick: () => handleDelete(item), variant: 'danger' },
 *     ]}
 *   />
 * </td>
 * ```
 */

export type BopRowActionVariant = 'default' | 'primary' | 'danger';

export interface BopRowAction {
  icon: React.ReactNode;
  label: string;                // Tooltip + aria-label (obbligatorio per accessibilità)
  onClick: () => void;
  variant?: BopRowActionVariant;
  disabled?: boolean;
  hidden?: boolean;             // Nasconde l'azione (es. per permessi)
}

interface Props {
  actions: BopRowAction[];
}

export default function BopRowActions({ actions }: Props) {
  const visible = actions.filter(a => !a.hidden);
  if (visible.length === 0) return null;

  return (
    <div className="bop-table__actions">
      {visible.map((action, i) => {
        const variantClass =
          action.variant === 'danger' ? 'bop-table__action-btn--danger' :
          action.variant === 'primary' ? 'bop-table__action-btn--primary' : '';
        return (
          <button
            key={i}
            type="button"
            className={`bop-table__action-btn ${variantClass}`.trim()}
            title={action.label}
            aria-label={action.label}
            disabled={action.disabled}
            onClick={(e) => {
              e.stopPropagation();
              if (!action.disabled) action.onClick();
            }}
          >
            {action.icon}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Icone standard riga (16×16, stroke 2) ──────────────────────────────── */

export const RowIcon = {
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  Trash: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  Copy: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  Download: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  Print: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  ),
  Disable: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  ),
  Send: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  More: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  ),
  Chevron: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
};
